import React, { useEffect, useState, useMemo, useRef } from "react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import 'ag-grid-enterprise';
import ConfirmDeleteModal from "../confirmdeletemodal";
import loaderGif from "../../../assets/loader.gif";
import api from '../../../config/axiosConfig';
import pdfIcon from "../../../assets/pdf-icon.png";

const Media = () => {
    const [formData, setFormData] = useState({
        id: null,  // Track if it's a new entry or an edit
        name: '',
        itemId: '',
        media: '',
        masterId: '',
        isactive: true,
        createddate: '',
        modifieddate: ''
    });
    const [rowData, setRowData] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false); // To track if we are in edit mode
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [files, setFiles] = useState([]);
    const [loader, setLoader] = useState(false);
    const [currentType, setCurrentType] = useState('items'); // Dropdown Type Selection
    const [dropdownOptions, setDropdownOptions] = useState([]);
    let [selectedMasterId, setSelectedMasterId] = useState(1); //For displaying item list by default

    const getData = async () => {
        try {
            const response = await api.get(`/media`);

            setRowData(response.data); // Populate the table with filtered data
            gridRef.current.api.setServerSideDatasource(datasource()); //Updates media list with selectedMasterId

        } catch (error) {
            console.error("Error fetching media data:", error);
        }
    };

    const fetchTableData = async () => {
        try {
            const response = await api.get(`/${currentType.toLowerCase()}`); // Fetch the respective data (items, programs, or residential)
            setDropdownOptions(response.data); // Update dropdown options for form
        } catch (error) {
            console.error("Error fetching dropdown data:", error);
        }
    };

    useEffect(() => {
        fetchTableData();
        getData();
    }, [currentType, selectedMasterId]);

    const gridRef = useRef();

    const handleFileChange = (e) => {
        setFiles(Array.from(e.target.files)); // Capture the selected file
    };

    // Column definitions for Ag-Grid
    const columnDefs = [

        { headerName: 'ID', field: 'id', sort: "desc", filter: 'agNumberColumnFilter' },
        { headerName: 'Name', field: 'name', filter: 'agTextColumnFilter' },
        { headerName: 'Item', field: 'itemId', filter: 'agNumberColumnFilter' },
        {
            headerName: 'Media',
            field: 'media',
            cellRenderer: (params) => {
                const filePath = params.data.path.replace('uploads/mediafiles/', '');
                const mediaUrl = `${process.env.REACT_APP_WS_URL}/api/media/mediafiles${filePath}`;

                if (!filePath || mediaUrl === `${process.env.REACT_APP_WS_URL}/api/media/mediafiles`) {
                    return (
                        <div style={{ textAlign: 'center', color: 'gray' }}>
                            No Media Present
                        </div>
                    );
                }

                // Check if the media URL is a video
                const isVideo = mediaUrl && (mediaUrl.includes('.mp4') || mediaUrl.includes('.webm'));

                if (isVideo) {
                    // Video renderer
                    return (
                        <video
                            src={mediaUrl}
                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                            muted
                        />
                    );
                } else if (mediaUrl.includes('.pdf')) {
                    // PDF renderer
                    return (
                        <a href={mediaUrl} target="_blank" rel="noopener noreferrer">
                            <img
                                src={pdfIcon}
                                alt="PDF"
                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                            />
                        </a>
                    );
                } else {
                    // Image renderer
                    return (
                        <img
                            src={mediaUrl}
                            alt="media"
                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                        />
                    );
                }
            },
        },
        { headerName: 'Is Active', field: 'isactive' },
        { headerName: 'Created By', field: 'createdby', filter: 'agTextColumnFilter' },
        { headerName: 'Created At', field: 'createddate', filter: 'agDateColumnFilter' },
        { headerName: 'Updated By', field: 'modifiedby', filter: 'agTextColumnFilter' },
        { headerName: 'Updated At', field: 'modifieddate', filter: 'agDateColumnFilter' },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: (params) => (
                <Button
                    variant="danger"
                    className="btn-sm"
                    // onClick={() => handleDelete(params.data)}
                >
                    <i className="bi bi-trash3"></i>
                </Button>
            ),
        },
    ];

    const sideBar = useMemo(() => {
        return {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                }
            ],
            defaultToolPanel: '',
        };
    }, []);

    const autoGroupColumnDef = {
        headerName: 'Group',
        width: 500,
        cellRenderer: 'agGroupCellRenderer',
    };

    const datasource = () => {
        return {
            getRows: async function (params) {
                try {
                    const { request } = params;

                    const response = await api.post(`/media/ag`, {
                        ...request,
                        type: currentType.toLowerCase(),
                        masterId: selectedMasterId
                    });

                    const { rows, lastRow } = response.data;

                    // Send data back to AG Grid
                    params.successCallback(rows, lastRow);
                } catch (error) {
                    console.error("Error fetching server-side data:", error);
                    params.failCallback();
                }
            }
        }
    };

    const gridOptions = {
        rowModelType: 'serverSide',
        columnDefs: columnDefs,
        rowHeight: 60,

        defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            enableValue: true,
            enableRowGroup: true,
            enablePivot: true,
            filterParams: {
                buttons: ["reset"],
                closeOnApply: true,
            },
        },

        sideBar: sideBar,
        rowGroupPanelShow: 'always',
        rowSelection: 'single',
        columnHoverHighlight: true,
        suppressRowGroupHidesColumns: true,
        cacheBlockSize: 20
    }

    // Attach the datasource when the grid is ready
    const onGridReady = (params) => {
        params.api.setServerSideDatasource(datasource());
    };

    // Row selection handler
    const onRowSelected = (event) => {
        if (event.node.isSelected()) {
            const selectedRow = event.data;

            let masterType = "";
            if (selectedRow.masterId === 1) masterType = "Items";
            else if (selectedRow.masterId === 2) masterType = "Programs";
            else if (selectedRow.masterId === 3) masterType = "Residential";

            setFormData({
                id: selectedRow.id,
                name: selectedRow.name,
                itemId: selectedRow.itemId,
                media: selectedRow.media,
                masterId: selectedRow.masterId,
                isactive: selectedRow.isactive
            });

            setCurrentType(masterType); 
            setSelectedMasterId(selectedRow.masterId);
            setIsEditMode(true);  // Switch to edit mode
            setSelectedRow(selectedRow)
        }
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle form submission for adding/updating media
    const handleAddData = async (e) => {
        e.preventDefault();
        setLoader(true);

        try {
            // Handle file upload
            let mediaPath = '';
            if (files.length) {
                const uploadFormData = new FormData();
                files.forEach((file) => uploadFormData.append('file', file));
                const uploadResponse = await api.post('/media/uploads', uploadFormData);
                mediaPath = uploadResponse.data.filePath; // Extract the uploaded file path
            }

            // Prepare media data for submission
            const mediaData = {
                name: formData.name,
                isactive: formData.isactive,
                itemId: formData.itemId || null,
                media: mediaPath,
                masterId: formData.masterId || null,
                createdat: new Date(),
            };

            // Determine whether to update or create a new entry
            const url = isEditMode ? `/media/${formData.id}` : '/media';
            const request = isEditMode ? api.put(url, mediaData) : api.post(url, mediaData);

            await request;

            // Clear form fields and reset edit mode
            setFormData(prevFormData => (
                { id: null, name: '', itemId: '', media: '', masterId: prevFormData.masterId, isactive: true }
            ));
            setFiles([]);
            setIsEditMode(false);
            setLoader(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });

            // Refresh rowData to reflect updates

        } catch (error) {
            console.error("Error adding/updating media:", error);
        } finally {
            setLoader(false);
        }
    };

    // Reset form for new entry
    const handleNewEntry = () => {
        setFormData({ id: null, name: '', itemId: '', media: '', masterId: '', isactive: true });
        setFiles([]);
        setIsEditMode(false);  // Switch to add mode
    };

    const handleDelete = () => {
        setShowModal(true);
    };

    const confirmDelete = async () => {
        try {
            await api.delete(`/media/${selectedRow.id}`);
            setShowModal(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });
        } catch (error) {
            console.error('Error deleting media:', error);
        }
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleTypeChange = (e) => {
        const selectedType = e.target.value;
        let masterId = null;
        if (selectedType === "Items") masterId = 1;
        else if (selectedType === "Programs") masterId = 2;
        else if (selectedType === "Residential") masterId = 3;

        setCurrentType(selectedType); // Update type
        setSelectedMasterId(masterId);
        setFormData(prevFormData => ({
            ...prevFormData,
            masterId: masterId, // Update masterId in formData
        }));
    };


    return (
        <>
            <Container fluid>
                <Row className="mt-4">
                    <h6 className="text-start">Media List</h6>
                    <div className="ag-theme-alpine" style={{ height: 400, width: '70%' }}>
                        <AgGridReact
                            ref={gridRef}
                            gridOptions={gridOptions}
                            onGridReady={onGridReady}
                            onRowSelected={onRowSelected}
                        />
                    </div>
                    <div style={{ width: '30%', height: '400px', border: '1px solid grey' }}>
                        {selectedRow && selectedRow.path ? (
                            selectedRow.path.includes('.mp4') || selectedRow.path.includes('.webm') ? (
                                <video
                                    src={`${process.env.REACT_APP_WS_URL}/api/media/mediafiles${selectedRow.path.replace('uploads/mediafiles/', '')}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '5px' }}
                                    controls
                                />
                            ) : selectedRow.path.includes('.pdf') ? (
                                <iframe
                                    src={`${process.env.REACT_APP_WS_URL}/api/media/mediafiles${selectedRow.path.replace('uploads/mediafiles/', '')}`}
                                    style={{ width: '100%', height: '100%', borderRadius: '5px' }}
                                    title="PDF Viewer"
                                />
                            ) : (
                                <img
                                    src={`${process.env.REACT_APP_WS_URL}/api/media/mediafiles${selectedRow.path.replace('uploads/mediafiles/', '')}`}
                                    alt="media"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '5px' }}
                                />
                            )
                        ) : null}
                    </div>


                </Row>
                <Row className="mt-4 border shadow p-4">
                    {loader && (
                        <div className="loader-overlay">
                            <img src={loaderGif} alt="Loading..." className="loader" />
                        </div>
                    )}
                    <Col md={12}>
                        <h6 className="text-start">{isEditMode ? "Edit Media" : "Add Media"}</h6>
                        <Form onSubmit={handleAddData}>
                            <Form.Group className="mb-3 d-flex align-items-center">
                                <Form.Label className="me-3">Name:</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    placeholder="Enter media name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="me-3"
                                    style={{ width: '50%' }}
                                />
                                <Form.Check
                                    type="checkbox"
                                    name="isactive"
                                    label="Is Active"
                                    checked={formData.isactive}
                                    onChange={handleChange}
                                    className="ms-4"
                                />
                            </Form.Group>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Master type</Form.Label>
                                        <Form.Select
                                            name="masterId"
                                            value={currentType}
                                            onChange={handleTypeChange}
                                        >
                                            <option value="Items">Items</option>
                                            <option value="Programs">Programs</option>
                                            <option value="Residential">Residential</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>{currentType.charAt(0).toUpperCase() + currentType.slice(1)}</Form.Label>
                                        <select
                                            name="itemId"
                                            value={formData.itemId}
                                            onChange={handleFormChange}
                                            className="form-select"
                                        >
                                            <option value="">Select Option</option>
                                            {dropdownOptions.map(option => (
                                                <option key={option.id} value={option.id}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </select>
                                    </Form.Group>
                                </Col>
                                <Col>
                                    {/* File Input for Media Upload */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Upload Photos/Videos</Form.Label>
                                        <Form.Control
                                            type="file"
                                            onChange={handleFileChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Button variant="primary" type="submit">
                                {isEditMode ? "Update" : "Add"}
                            </Button>
                            {isEditMode ?
                                <Button variant="secondary" className="ms-3" onClick={handleNewEntry}>
                                    Create New Entry
                                </Button>
                                : ''
                            }
                        </Form>
                    </Col>
                </Row>
            </Container>

            {/* Modal for confirming deletion */}
            <ConfirmDeleteModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={confirmDelete}
                title="Confirm Deletion"
                body={`Are you sure you want to delete ${selectedRow?.name}?`}
                confirmText="Delete"
            />


        </>
    );
};

export default Media; 